import { Buffer } from 'buffer';

import { useMemo, useState } from 'react';

import { Button, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useField } from 'formik';
import { FileRejection, useDropzone } from 'react-dropzone';

import { useMutation } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import { showErrorToast } from 'src/components/Toasts/toast';
import { mimeTypes } from 'src/constants';
import { GET_PRESIGNED_URL } from 'src/graphql/mutations/getPresignedURL.mutation';
import { UPLOAD_PDF } from 'src/graphql/mutations/uploadPdf.mutation';

import { OptionTypes } from '../DropDown';
import FilePreviewer from '../FileUploadDropDown/file-previewer';

interface FileUploadSelectProps {
    name: string;
    label?: string;
    fileTypes?: OptionTypes[];
    placeholder?: string;
    maxFileSize?: number;
    acceptedFileTypes?: { [key: string]: string[] };
    isReadOnly?: boolean;
    landscapeImagesOnly?: boolean;
    isNonSensitive?: boolean;
    callback?: (signedUrl: string, urlOnly?: string, filename?: string) => void;
}
const byteToMB = (size: number) => {
    return (size / 1000000).toFixed(2);
};
const FileUploadSelect = ({
    name,
    label,
    maxFileSize = 20000000,
    acceptedFileTypes = mimeTypes.images,
    isReadOnly,
    landscapeImagesOnly,
    isNonSensitive = false,
    callback,
}: FileUploadSelectProps) => {
    const theme = useTheme();
    const [{ value: file }, { error, touched }, { setValue: setFile }] = useField(name);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const [getPresignedURL] = useMutation(GET_PRESIGNED_URL, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const [uploadPdf] = useMutation(UPLOAD_PDF, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });
    const readFileAsBuffer = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (reader.result instanceof ArrayBuffer) {
                    resolve(Buffer.from(reader.result));
                } else {
                    reject(new Error('Expected ArrayBuffer as result'));
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    const hasError = useMemo(() => {
        return error && touched;
    }, [error, touched]);

    const onDrop = async (acceptedFiles, fileRejections: FileRejection[]) => {
        const supportedFilesErrorMessage = fileRejections[0]?.errors[0]?.message;
        try {
            if (supportedFilesErrorMessage?.length > 0) {
                showErrorToast(supportedFilesErrorMessage);
                return;
            }
            setUploading(true);
            const acceptedFile = acceptedFiles[0];
            const { name, type, size } = acceptedFile;

            if (size > maxFileSize) {
                const sizeInMb = byteToMB(size);
                const maxInMb = byteToMB(maxFileSize);
                const errorMsg = `File size of ${sizeInMb}MB exceeds the maximum allowable size of ${maxInMb}MB`;
                showErrorToast(errorMsg);
                return;
            }

            if (type.startsWith('image/') && landscapeImagesOnly) {
                const reader = new FileReader();

                const isLandscape = await new Promise((resolve, reject) => {
                    reader.onload = (event) => {
                        const { result } = reader;

                        if (typeof result === 'string') {
                            const img = new Image();

                            img.onload = () => {
                                const { width, height } = img;
                                resolve(width > height);
                            };
                            img.src = result;
                        } else {
                            reject(new Error('FileReader result is not a string.'));
                        }
                    };

                    reader.onerror = () => {
                        reject(new Error('Error reading the file.'));
                    };

                    reader.readAsDataURL(acceptedFile);
                });
                if (!isLandscape) {
                    showErrorToast(
                        'The format of the image needs to be in landscape dimensions only.'
                    );
                    return;
                }
            }

            const fileUpload = {
                mimeType: type,
                fileName: name,
                fileSize: size,
                locationUrl: '',
            };

            let newFile = acceptedFile;

            if (type === 'application/pdf') {
                const buffer = await readFileAsBuffer(acceptedFile);
                const convertedBuffer = buffer.toString('base64');
                const { data } = await uploadPdf({
                    variables: {
                        buffer: convertedBuffer,
                    },
                });
                const compressedBuffer = Buffer.from(data.uploadPdf.compressedBuffer, 'base64');
                newFile = new File([compressedBuffer], `${name}.pdf`, {
                    type: 'application/pdf',
                });
            }

            const { data } = await getPresignedURL({
                variables: {
                    input: {
                        fileName: name,
                        mimeType: fileUpload.mimeType,
                        fileSize: fileUpload.fileSize,
                        hasBackup: false,
                        isNonSensitive,
                        referer: window.location.pathname.substring(1),
                    },
                },
            });

            const signedUrl = data?.getSecureUploadUrl?.signedUrl;
            if (signedUrl) {
                await fetch(signedUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': type },
                    body: newFile,
                });
                const [baseUrl] = data.getSecureUploadUrl.viewableSignedUrl.split('?');
                fileUpload.locationUrl = baseUrl;
                setFile(fileUpload.locationUrl);
                setUploadedFile({ ...newFile, ...fileUpload });

                if (callback) {
                    const [urlOnly] = signedUrl.split('?');
                    callback(signedUrl, urlOnly, fileUpload.fileName);
                }
            }
        } catch (error) {
            showErrorToast('Failed to upload file', error);
        } finally {
            setUploading(false);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        maxFiles: 1,
        multiple: false,
        accept: acceptedFileTypes,
    });

    return (
        <Flex flexDirection="column" gap="8px">
            {label && (
                <Typography
                    color={theme.palette.text.primary}
                    fontWeight={600}
                    fontSize="14px"
                    marginBottom="13px"
                >
                    {label}
                </Typography>
            )}
            <Flex
                padding="8px"
                borderRadius="8px"
                border={`1px solid ${theme.palette.text.secondary}`}
                maxHeight="53px"
                alignItems="center"
                overflow="hidden"
                justifyContent={'end'}
                {...getRootProps()}
            >
                <input {...getInputProps()} data-testid="file-input" />
                <Button
                    variant="contained"
                    sx={{
                        color: '#fff',
                        borderRadius: '8px',
                        minWidth: '100px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    }}
                    component="span"
                    disabled={isReadOnly && uploading}
                >
                    Select File
                </Button>
            </Flex>
            {uploading && <LinearProgress />}
            {(uploadedFile?.locationUrl && uploadedFile?.fileName) || file ? (
                <FilePreviewer
                    onRemove={
                        isReadOnly
                            ? undefined
                            : () => {
                                  setUploadedFile(null);
                                  setFile('');
                              }
                    }
                />
            ) : null}
            {hasError && <label className="error">{error}</label>}
        </Flex>
    );
};

export default FileUploadSelect;
